import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface JobStatusParameters {
  user_id?: number;
  account_id?: number;
  recipients?: string[];
  schedule_id?: number;
  format?: string;
  context?: Record<string, unknown>;
  graphql_context?: {
    query?: string;
    format?: string;
    user_id?: number;
    account_id?: number;
    report_layout_id?: number;
  };
}

export interface JobStatus {
  id: number;
  meta: Record<string, unknown>;
  account_id: number;
  user_id: number;
  can_regenerate: boolean;
  completed_at: string | null;
  errored_at: string | null;
  job_class: string;
  result_data: unknown | null;
  source_name: string;
  source_url: string;
  time_taken: string;

  // Retained for partial Pusher updates from the legacy job-status payload.
  object_name?: string;
  label?: string;
  date_requested?: string;
  started_at: string;
  status: 'enqueued' | 'in_progress' | 'generating' | 'complete' | 'failed' | 'expired';
  export_format?: string;
  download_url?: string;
  latest_document_name: string;
  latest_document_size: number;
  latest_document_url?: string;
  job_purpose: string;
  parameters?: JobStatusParameters;
}

export interface JobStatusResponse {
  job_statuses: JobStatus[];
  count: number;
  offset: number;
  next: string | null;
  previous: string | null;
}

export interface RegenerateJobResponse {
  message: string;
  async: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class JobsStatusService {
  constructor(private readonly httpClient: HttpClient) {}

  getJobsStatus(page: number = 1, per_page: number = 20): Observable<JobStatusResponse> {
    const params = new HttpParams()
      .append('job_purpose[]', 'object_export')
      .append('job_purpose[]', 'object_report')
      .append('page', page.toString())
      .append('per_page', per_page.toString());

    return this.httpClient.get<JobStatusResponse>('job_statuses', { params });
  }

  getJobStatusById(id: number): Observable<JobStatus> {
    const params = new HttpParams().append('job_purpose[]', 'object_export').append('job_purpose[]', 'object_report');

    return this.httpClient
      .get<{ job_status: JobStatus }>(`job_statuses/${id}`, { params })
      .pipe(map((response) => response.job_status));
  }

  regenerateJob(id: number): Observable<RegenerateJobResponse> {
    return this.httpClient
      .post<{ data: RegenerateJobResponse }>(`job_statuses/${id}/regenerate`, {})
      .pipe(map((response) => response.data));
  }
}
